import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ClientProfile {
  id: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

// Fetch multiple client profiles by IDs
export const useClientProfiles = (clientIds: string[]) => {
  return useQuery({
    queryKey: ['client-profiles', clientIds],
    queryFn: async (): Promise<Record<string, ClientProfile>> => {
      if (!clientIds || clientIds.length === 0) return {};

      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name, email')
        .in('id', clientIds);

      if (error) throw error;
      
      // Convert array to object keyed by id for easy lookup
      const profileMap: Record<string, ClientProfile> = {};
      (data || []).forEach((profile) => {
        profileMap[profile.id] = profile;
      });
      
      return profileMap;
    },
    enabled: clientIds && clientIds.length > 0,
  });
};

// Get display name from profile
export const getClientDisplayName = (profile: ClientProfile | undefined): string => {
  if (!profile) return 'Unknown Client';
  
  if (profile.full_name) return profile.full_name;
  if (profile.first_name && profile.last_name) {
    return `${profile.first_name} ${profile.last_name}`;
  }
  if (profile.first_name) return profile.first_name;
  if (profile.email) return profile.email;
  
  return 'Unknown Client';
};
